import { UseMutationResult, UseQueryResult, useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { AxiosResponse } from "axios";

import {
  editReservationEndpoint,
  getCreateReservationEndpoint,
  getCreateRecurringReservationEndpoint,
  getGetAllReservationsEndpoint,
  getGetAvailableAppointmentsEndpoint,
  getGetReservationByEndpoint,
  changeEmployeeEndpoint,
  getSearchReservationsEndpoint,
  getCancelInfoEndpoint,
  cancelChainEndpoint,
  getNoShowInfoEndpoint,
} from "../endpoints";
import { httpClient } from "../network";
import { toast } from "react-toastify";
import {
  getReservationApiError,
  isSlotNoLongerAvailableError,
  SLOT_NO_LONGER_AVAILABLE_MESSAGE,
} from "../../utils/reservationErrors";
import {
  ICreateReservationRequest,
  ICreateReservationResponse,
  IEditReservationRequest,
  IEditReservationResponse,
  IGetAllReservationsRequest,
  IGetAllReservationsResponse,
  IGetAvailableAppointmentsRequest,
  IGetAvailableAppointmentsResponse,
  IGetReservationByRequest,
  IGetReservationByResponse,
  CreateRecurringReservationRequestDto,
  CreateRecurringReservationResponseDto,
  ISearchReservationsRequest,
  IGetCancelInfoResponse,
  ICancelChainRequest,
  ICancelChainResponse,
  IGetNoShowInfoResponse,
} from "../../../types";

export const useCreateReservationService = (): UseMutationResult<ICreateReservationResponse, unknown, ICreateReservationRequest> => {
  const queryClient = useQueryClient();
  const mutation = useMutation({
    mutationFn: async (createReservationPayload: ICreateReservationRequest) => {
      const response: AxiosResponse<ICreateReservationResponse> = await httpClient.post(getCreateReservationEndpoint(), createReservationPayload);
      return response.data;
    },
    onSuccess: (data) => {
      toast.success("Rezervacija je uspješno kreirana");
      if (data.clientBlockWarning) {
        toast.warning(data.clientBlockWarning);
      }
      queryClient.invalidateQueries({ queryKey: ["getAllReservations"] });
      queryClient.invalidateQueries({ queryKey: ["getAdminPanelStatistic"] });
    },
    onError: (error: unknown) => {
      if (isSlotNoLongerAvailableError(error)) {
        toast.error(SLOT_NO_LONGER_AVAILABLE_MESSAGE);
        return;
      }
      const { message } = getReservationApiError(error);
      toast.error(message || "Rezervacija nije uspješno kreirana");
    },
  });

  return mutation;
};

export const useCreateRecurringReservationService = (): UseMutationResult<
  CreateRecurringReservationResponseDto,
  unknown,
  CreateRecurringReservationRequestDto
> => {
  const queryClient = useQueryClient();
  const mutation = useMutation({
    mutationFn: async (createRecurringReservationPayload: CreateRecurringReservationRequestDto) => {
      const response: AxiosResponse<CreateRecurringReservationResponseDto> = await httpClient.post(
        getCreateRecurringReservationEndpoint(),
        createRecurringReservationPayload
      );
      return response.data;
    },
    onSuccess: (data) => {
      const { successCount, totalCount, skippedCount } = data.data;
      if (skippedCount === 0) {
        toast.success(`Rekurzivna serija uspješno kreirana! ${successCount} termina rezervisano.`);
      } else {
        toast.warning(
          `Rekurzivna serija djelimično kreirana. ${successCount} od ${totalCount} termina rezervisano. ${skippedCount} preskočeno.`
        );
      }
      if (data.data.clientBlockWarning) {
        toast.warning(data.data.clientBlockWarning);
      }
      queryClient.invalidateQueries({ queryKey: ["getAllReservations"] });
      queryClient.invalidateQueries({ queryKey: ["getAdminPanelStatistic"] });
    },
    onError: (error: any) => {
      const errorMessage = error?.response?.data?.message || "Greška pri kreiranju rekurzivne serije.";
      toast.error(errorMessage);
    },
  });

  return mutation;
};

export const useEditReservationService = (id: string, isDiscount?: boolean, skipInvalidation?: boolean) => {
  const queryClient = useQueryClient();
  const mutation = useMutation({
    mutationKey: ["editReservation"],
    mutationFn: async (editReservationPayload: IEditReservationRequest) => {
      let sendEmail = true;
      if(editReservationPayload.sendEmail !== undefined) {
        sendEmail = editReservationPayload.sendEmail;
      } else {
        sendEmail = true;
      }

      const response: AxiosResponse<IEditReservationResponse> = await httpClient.put(editReservationEndpoint(id), { ...editReservationPayload, sendEmail });
      return response.data;
    },
    onSuccess: () => {
      if(!skipInvalidation) {
        if(!isDiscount) {
          queryClient.resetQueries();
        } else {
          queryClient.invalidateQueries({ queryKey: ["getReservationBy"] });
        }
      }
      toast.success("Rezervacija je uspješno uređena");
    },
    onError: () => {
      toast.error("Rezervacija nije uspješno uređena")
    },
  });

  return mutation;
};

export const useGetAvailableAppointmentsService = (): UseMutationResult<
  IGetAvailableAppointmentsResponse,
  unknown,
  IGetAvailableAppointmentsRequest
> => {
  const mutation = useMutation({
    mutationFn: async (getAvailableAppointmentsPayload: IGetAvailableAppointmentsRequest) => {
      const response = await httpClient.post(getGetAvailableAppointmentsEndpoint(getAvailableAppointmentsPayload), getAvailableAppointmentsPayload);
      return response.data;
    },
  });

  return mutation;
};

export const useGetAllReservationsService = (
  getAllReservationsQueries: IGetAllReservationsRequest
): UseQueryResult<IGetAllReservationsResponse, unknown> => {
  const toDateKey = (value: Date | string | undefined) => {
    if (!value) return "";

    if (typeof value === "string" && /^\d{4}-\d{2}-\d{2}/.test(value)) {
      return value.slice(0, 10);
    }

    const parsed = new Date(value);
    if (Number.isNaN(parsed.getTime())) {
      return "";
    }

    const year = parsed.getFullYear();
    const month = String(parsed.getMonth() + 1).padStart(2, "0");
    const day = String(parsed.getDate()).padStart(2, "0");
    return `${year}-${month}-${day}`;
  };

  const query = useQuery({
    queryKey: ["getAllReservations", getAllReservationsQueries],
    queryFn: async () => {
      const fromDateKey = toDateKey(getAllReservationsQueries?.conditions?.from);
      const toDateKeyValue =
        toDateKey(getAllReservationsQueries?.conditions?.to) || fromDateKey;

      const requestQueries: IGetAllReservationsRequest = {
        ...getAllReservationsQueries,
        conditions: {
          from: `${fromDateKey}T00:00:00.000Z`,
          to: `${toDateKeyValue}T23:59:59.000Z`,
        },
      };

      const response: AxiosResponse<IGetAllReservationsResponse> = await httpClient.get(
        getGetAllReservationsEndpoint(requestQueries)
      );
      return response.data;
    },
    retry: 0,
  });
  return query;
};

export const useGetReservationByService = (getReservationByQueries: IGetReservationByRequest): UseQueryResult<IGetReservationByResponse, unknown> => {
  const query = useQuery({
    queryKey: ["getReservationBy", getReservationByQueries],
    queryFn: async () => {
      const response: AxiosResponse<IGetReservationByResponse> = await httpClient.get(getGetReservationByEndpoint(getReservationByQueries));
      return response.data;
    },
    retry: 0,
  });
  return query;
};

export const useSearchReservationsService = (
  searchParams: ISearchReservationsRequest
): UseQueryResult<IGetAllReservationsResponse, unknown> => {
  const query = useQuery({
    queryKey: ["searchReservations", searchParams],
    queryFn: async () => {
      const response: AxiosResponse<IGetAllReservationsResponse> = await httpClient.get(
        getSearchReservationsEndpoint(searchParams)
      );
      return response.data;
    },
    enabled: searchParams.search.length >= 2,
    retry: 0,
  });
  return query;
};

/**
 * Bulk-cancels linked reservations. The backend sends ONE aggregated
 * Viber/email notification listing all canceled services.
 */
export const useCancelChainService = (id: string): UseMutationResult<ICancelChainResponse, unknown, ICancelChainRequest> => {
  const queryClient = useQueryClient();
  const mutation = useMutation({
    mutationKey: ["cancelChain"],
    mutationFn: async (payload: ICancelChainRequest) => {
      const response: AxiosResponse<ICancelChainResponse> = await httpClient.put(cancelChainEndpoint(id), {
        ...payload,
        sendEmail: payload.sendEmail ?? true,
      });
      return response.data;
    },
    onSuccess: (data) => {
      queryClient.resetQueries();
      toast.success(`Otkazano termina: ${data.data.canceledCount}`);
    },
    onError: () => {
      toast.error("Otkazivanje vezanih rezervacija nije uspjelo");
    },
  });
  return mutation;
};

export const useGetCancelInfoService = (id: string): UseQueryResult<IGetCancelInfoResponse, unknown> => {
  const query = useQuery({
    queryKey: ["getCancelInfo", id],
    queryFn: async () => {
      const response: AxiosResponse<IGetCancelInfoResponse> = await httpClient.get(getCancelInfoEndpoint(id));
      return response.data;
    },
    enabled: !!id,
    retry: 0,
  });
  return query;
};

export const useGetNoShowInfoService = (id: string, enabled: boolean = true): UseQueryResult<IGetNoShowInfoResponse, unknown> => {
  const query = useQuery({
    queryKey: ["getNoShowInfo", id],
    queryFn: async () => {
      const response: AxiosResponse<IGetNoShowInfoResponse> = await httpClient.get(getNoShowInfoEndpoint(id));
      return response.data;
    },
    enabled: enabled && !!id,
    retry: 0,
  });
  return query;
};

export const useChangeEmployeeService = (id: string) => {
  const queryClient = useQueryClient();
  const mutation = useMutation({
    mutationKey: ["changeEmployee"],
    mutationFn: async (payload: { newStaffId: string }) => {
      const response: AxiosResponse<IEditReservationResponse> = await httpClient.put(changeEmployeeEndpoint(id), payload);
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["getReservationBy"] });
      queryClient.invalidateQueries({ queryKey: ["getAllReservations"] });
      toast.success("Zaposlenik na terminu uspješno promijenjen");
    },
    onError: (error: any) => {
      const errorMessage = error?.response?.data?.message || "Greška pri promjeni zaposlenika";
      toast.error(errorMessage);
    },
  });

  return mutation;
};
